import { cn } from "../cn";

export type RingMeterTone = "accent" | "warning" | "critical" | "series-2" | "info";

const STROKE: Record<RingMeterTone, string> = {
  accent: "stroke-accent",
  warning: "stroke-warning",
  critical: "stroke-critical",
  "series-2": "stroke-chart-2",
  info: "stroke-info",
};

const SIZES = {
  sm: { box: 28, width: 3 },
  md: { box: 64, width: 7 },
  lg: { box: 88, width: 9 },
} as const;

export interface RingMeterProps {
  /** 0–1 of the ring. Clamped, with the same 1.5% floor as `Meter`. */
  value: number;
  tone?: RingMeterTone;
  size?: keyof typeof SIZES;
  /**
   * Only when the ring is the sole statement of the figure. Every billing
   * screen prints the number beside it, so the default is `aria-hidden`.
   */
  label?: string;
  className?: string;
}

/**
 * A meter drawn as a ring, for the one figure a card leads with.
 *
 * `Meter` is the bar inside a row somebody else lays out; this is the mark a
 * headline number sits beside, so the eye reads how full something is before
 * it reads the number.
 */
export function RingMeter({
  value,
  tone = "accent",
  size = "md",
  label,
  className,
}: RingMeterProps) {
  const { box, width } = SIZES[size];
  const r = (box - width) / 2;
  const circumference = 2 * Math.PI * r;
  const v = Math.min(1, Math.max(0.015, Number.isFinite(value) ? value : 0));
  return (
    <svg
      width={box}
      height={box}
      viewBox={`0 0 ${String(box)} ${String(box)}`}
      role={label === undefined ? undefined : "img"}
      aria-label={label}
      aria-hidden={label === undefined ? "true" : undefined}
      className={cn("shrink-0 -rotate-90", className)}
    >
      <circle
        cx={box / 2}
        cy={box / 2}
        r={r}
        fill="none"
        strokeWidth={width}
        className="stroke-sunk"
      />
      <circle
        cx={box / 2}
        cy={box / 2}
        r={r}
        fill="none"
        strokeWidth={width}
        strokeLinecap="round"
        strokeDasharray={`${(v * circumference).toFixed(2)} ${circumference.toFixed(2)}`}
        className={STROKE[tone]}
      />
    </svg>
  );
}
